import type { InventoryResponse } from "../../../api/schemas";

type Items = NonNullable<InventoryResponse["items"]>;

/** Inventory rows belong to the requested owner (the DTO excludes burned rows). */
export function assertInventoryPageIdentity(page: InventoryResponse, expected: {
  owner: string | null; chainId?: number | null | undefined; collection?: string | null | undefined; sourceToken?: string | null;
}): void {
  if (!sameAddress(page.owner, expected.owner) || page.items?.some(item => !sameAddress(item.owner, expected.owner))) {
    throw new Error("INVENTORY_OWNER_MISMATCH");
  }
  if (expected.chainId != null && page.chainId !== expected.chainId ||
    expected.collection && !sameAddress(page.collectionAddress, expected.collection) ||
    page.items?.some(item => item.chainId !== page.chainId || !sameAddress(item.collectionAddress, page.collectionAddress) ||
      expected.sourceToken && !sameAddress(item.sourceToken, expected.sourceToken))) {
    throw new Error("INVENTORY_DEPLOYMENT_MISMATCH");
  }
}

// Local provenance cannot be supplied by JSON. The item's own snapshot is its
// last lifecycle event, not necessarily the block used to read canonical media.
const readSnapshot = Symbol("inventory page snapshot");
type RetainedItem = Items[number] & { [readSnapshot]?: InventoryResponse["snapshot"] };

export function retainInventoryPage(page: InventoryResponse): InventoryResponse {
  return { ...page, items: page.items?.map((item) => ({ ...item, [readSnapshot]: page.snapshot })) ?? null };
}

export function inventoryItemSnapshot(item: Items[number], fallback: InventoryResponse["snapshot"]) {
  const retained = item as RetainedItem;
  return readSnapshot in retained ? retained[readSnapshot] ?? null : fallback;
}

const itemKey = (item: Items[number]) => `${item.collectionAddress.toLowerCase()}:${item.tokenId}`;

/** Replace re-read tail records in place and append unseen IDs, including within a page. */
export function appendInventoryItems(previous: Items, incoming: Items): Items {
  const updates = new Map(incoming.map((item) => [itemKey(item), item]));
  const result = previous.map((item) => {
    const key = itemKey(item);
    const next = updates.get(key) ?? item;
    updates.delete(key);
    return next;
  });
  return [...result, ...updates.values()];
}

/** Live first page wins duplicates; retained tail records keep their read provenance. */
export function inventoryDisplay(head: InventoryResponse, tail: Items, continuation: InventoryResponse): InventoryResponse {
  const first = appendInventoryItems([], head.items ?? []);
  const seen = new Set(first.map(itemKey));
  return { ...head, nextCursor: continuation.nextCursor,
    items: [...first, ...tail.filter((item) => !seen.has(itemKey(item)))] };
}

export function sameInventorySnapshot(left: InventoryResponse, right: InventoryResponse): boolean {
  return sameInventoryIdentity(left, right) && Boolean(left.snapshot && right.snapshot &&
      left.snapshot.blockNumber === right.snapshot.blockNumber &&
      left.snapshot.blockHash.toLowerCase() === right.snapshot.blockHash.toLowerCase());
}

export function sameInventoryIdentity(left: InventoryResponse, right: InventoryResponse): boolean {
  return left.chainId === right.chainId && sameAddress(left.owner, right.owner) &&
    sameAddress(left.collectionAddress, right.collectionAddress);
}

function sameAddress(left: string | null | undefined, right: string | null | undefined): boolean {
  return Boolean(left && right && left.toLowerCase() === right.toLowerCase());
}

/**
 * Merge a cursor page only into the exact inventory snapshot that issued it.
 * A delayed page from snapshot A must never replace a newer root snapshot B.
 */
export function mergeInventoryPage(
  previous: InventoryResponse | null,
  page: InventoryResponse,
  expectedOwner: string,
): InventoryResponse | null {
  if (!previous || !previous.inventoryAvailable || !page.inventoryAvailable ||
    previous.items === null || page.items === null ||
    !sameAddress(previous.owner, expectedOwner) || !sameAddress(page.owner, expectedOwner) ||
    !sameAddress(previous.collectionAddress, page.collectionAddress) ||
    !sameInventorySnapshot(previous, page)) {
    return previous;
  }

  return { ...page, items: appendInventoryItems(previous.items, page.items) };
}
